import { ChangeEvent, useState } from 'react';
import { Collapse } from '@material-ui/core';
import { AlternateEmail, Remove, Close } from '@material-ui/icons';
import { TransitionGroup } from 'react-transition-group';
import { Modal, ModalContent } from '../../components/Modal';
import { Button } from '../../components/Button/NewButton';
import { Input } from '../../components/Input';
import { ListItemText } from '../../components/ListItemText';
import { IconButton } from '../../components/IconButton';
import { isEmailValid } from '../../lib/validators';
import clsx from 'clsx';

interface Props {
  ccList: string[];
  updateCcList(array: string[]): void;
  maxWidth: 'md' | 'lg';
  onClose(): void;
  open: boolean;
  portalOut?: Element;
}

export const CcModal = ({
  ccList,
  maxWidth,
  onClose,
  open,
  portalOut,
  updateCcList,
}: Props) => {
  const [newContact, setNewContact] = useState('');

  const handleClose = () => {
    onClose();
    setNewContact('');
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setNewContact(event.target.value);
  };

  const removeFromCc = (contact: string) => {
    updateCcList(ccList.filter((c) => c !== contact));
  };

  const addToCc = () => {
    updateCcList([...(ccList ?? []), newContact]);
    setNewContact('');
  };

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      maxWidth={maxWidth}
      portalOut={portalOut}
      hideCloseButton
    >
      <div className="inline-flex items-center justify-between w-full py-2 pl-4 pr-2 bg-background-dark">
        <h5 className="text-white text-lg">Add Contacts</h5>{' '}
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </div>
      <div
        className={clsx('border-border-light dark:border-border-dark', {
          'border-b': ccList.length,
        })}
      >
        {ccList.length > 0 ? (
          <ul className="p-6 max-h-[300px] overflow-y-auto">
            <TransitionGroup>
              {ccList.map((contact: any, index: number) => {
                return (
                  <Collapse key={`cc-${index + 1}`}>
                    <li className="w-full inline-flex items-center justify-between">
                      <AlternateEmail />
                      <ListItemText
                        classes={{
                          primaryText:
                            'whitespace-nowrap max-w-full overflow-hidden text-ellipsis px-2',
                        }}
                        primary={contact}
                      />
                      <IconButton onClick={() => removeFromCc(contact)}>
                        <Remove />
                      </IconButton>
                    </li>
                  </Collapse>
                );
              })}
            </TransitionGroup>
          </ul>
        ) : null}
      </div>
      <ModalContent className="grid grid-cols-1 gap-4">
        <div className="inline-flex items-center gap-2">
          <Input
            label="E-Mail Address:"
            type="email"
            fullWidth
            placeholder="user@email.com"
            autoFocus
            helperText={
              !isEmailValid(newContact) ? 'Invalid e-mail address' : undefined
            }
            error={!isEmailValid(newContact)}
            onChange={handleChange}
            value={newContact}
          />
          {newContact.length ? (
            <IconButton onClick={() => setNewContact('')}>
              <Close />
            </IconButton>
          ) : null}
        </div>
        <Button
          fullWidth
          theme="action"
          onClick={() => addToCc()}
          disabled={!isEmailValid(newContact)}
        >
          Add
        </Button>
      </ModalContent>
    </Modal>
  );
};
